import React from 'react';
import { Box, Typography, IconButton, Tooltip, useTheme, alpha } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';

interface DocumentTypeTabsProps {
  availableDocTypes: string[];
  selectedDocType: string;
  onDocTypeChange: (docType: string) => void;
  onAddDocTypeClick: () => void;
  availableDocTypeOptions: string[];
}

const DocumentTypeTabs: React.FC<DocumentTypeTabsProps> = ({
  availableDocTypes,
  selectedDocType,
  onDocTypeChange,
  onAddDocTypeClick,
  availableDocTypeOptions
}) => {
  const theme = useTheme();

  if (!availableDocTypes || availableDocTypes.length === 0) return null;

  return (
    <Box sx={{
      mb: 3,
      borderBottom: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
      borderRadius: '8px 8px 0 0',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      overflow: 'hidden'
    }}>
      <Typography
        variant="h6"
        sx={{
          p: 2,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: alpha(theme.palette.primary.main, 0.04)
        }}
      >
        <DescriptionIcon sx={{ mr: 1.5, color: theme.palette.primary.main }} />
        Document Categories
      </Typography>
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        overflowX: 'auto'
      }}>
        {availableDocTypes.map((docType) => (
          <Box
            key={docType}
            component="button"
            onClick={() => onDocTypeChange(docType)}
            sx={{
              flex: '1 0 auto',
              minWidth: 'fit-content',
              border: 'none',
              borderBottom: '3px solid',
              borderColor: selectedDocType === docType ? 'primary.main' : 'transparent',
              backgroundColor: selectedDocType === docType ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              color: selectedDocType === docType ? 'primary.main' : 'text.secondary',
              p: 2,
              textTransform: 'none',
              fontWeight: selectedDocType === docType ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                color: selectedDocType === docType ? 'primary.main' : 'text.primary',
              }
            }}
          >
            {docType}
          </Box>
        ))}
        {availableDocTypeOptions.length > 0 && (
          <Tooltip title="Add New Document Type">
            <IconButton
              color="primary"
              onClick={onAddDocTypeClick}
              sx={{
                p: 2,
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default DocumentTypeTabs;